import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { landxmlMergeTrackingAtom } from '@/atoms/landxmlMerge-progress.atom';
import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { batchByproductsQuery } from '@/queries/byproduct.query';
import { layersQuery } from '@/queries/layer.query';
import { navigateToDownload } from '@/lib/analyses/navigateToDownload';
import { landxmlMergeWarningTexts, type LandxmlMergeStatsWarnings } from '@/lib/analyses/landxmlMergeWarnings';

/** Keep a merge pending until postprocess creates its independent layer. */
export function useLandxmlMergeProgress(projectId: string) {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const [trackers, setTrackers] = useAtom(landxmlMergeTrackingAtom);
  const hasTrackers = trackers.some((tracker) => tracker.projectId === projectId);
  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });
  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });
  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    const tick = () => {
      void refetchJobs({ cancelRefetch: false });
      void refetchLayers({ cancelRefetch: false });
    };
    tick();
    const timer = window.setInterval(tick, 2000);
    return () => window.clearInterval(timer);
  }, [hasTrackers, projectId, refetchJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];
    const layers = layersData?.layers ?? [];
    setTrackers((current) => {
      let changed = false;
      const next = current.filter((tracker) => {
        if (tracker.projectId !== projectId) return true;
        const failed = jobs.find((item) => item.batchId === tracker.batchId && item.status === 'FAILED');
        if (failed) {
          toast.error(failed.errorLogs?.trim() || t('landxmlMerge.failed'), { id: tracker.toastId, duration: 8000 });
          changed = true;
          return false;
        }
        const layer = layers.find((item) => {
          const metadata = (item.metadata ?? {}) as { batchId?: string; type?: string };
          return metadata.batchId === tracker.batchId && metadata.type === 'LANDXML';
        });
        if (!layer) return true;
        const metadata = (layer.metadata ?? {}) as { sourceProcessJobId?: string; landxmlMergeStats?: LandxmlMergeStatsWarnings };
        const stats = metadata.landxmlMergeStats;
        const warnings = landxmlMergeWarningTexts(stats, t);
        void queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
        toast.success(t('landxmlMerge.completed', { name: tracker.outputName }) +
          (warnings.length ? ` ${t('landxmlMerge.warnings', { count: warnings.length })}` : ''), {
          id: tracker.toastId,
          duration: 10000,
          action: {
            label: t('landxmlMerge.download'),
            onClick: async () => {
              try {
                const result = await execute(batchByproductsQuery, { id: tracker.batchId });
                const file = result.batch?.byproducts?.find((item) => item.processJobId === metadata.sourceProcessJobId);
                if (file) navigateToDownload(file.downloadUrl);
                else toast.error(t('landxmlMerge.downloadMissing'));
              } catch (error) {
                toast.error(error instanceof Error ? error.message : String(error));
              }
            },
          },
        });
        changed = true;
        return false;
      });
      return changed ? next : current;
    });
  }, [hasTrackers, jobsData, layersData, projectId, queryClient, setTrackers, t]);
}
